/**
 * IndexedDB Manager for Offline Storage
 * Handles storage of cached data, credentials, and sync outbox
 */

const DB_NAME = 'abbeyzone_offline';
const DB_VERSION = 4;

// Store names — platform-wide offline capability stores
const STORES = {
  CREDENTIALS: 'credentials',
  JOBS: 'jobs',
  SITES: 'sites', 
  CLIENTS: 'clients',
  SERVICES: 'services',
  OUTBOX: 'outbox',
  META: 'meta',
  // Phase 3 — Stocktake offline stores
  STOCKTAKE_PACKAGES: 'stocktake_packages',
  SYNC_QUEUE: 'sync_queue',
  // Phase A — Count Mode stores
  COUNT_PACKAGES: 'count_packages',
  // Phase C — Observations stores
  OBSERVATIONS: 'observations',
  PHOTO_QUEUE: 'photo_queue',
};

let dbInstance = null;

/**
 * Initialize IndexedDB
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Credentials store - encrypted login verifier
      if (!db.objectStoreNames.contains(STORES.CREDENTIALS)) {
        db.createObjectStore(STORES.CREDENTIALS, { keyPath: 'email' });
      }

      // Jobs store - cached schedule data
      if (!db.objectStoreNames.contains(STORES.JOBS)) {
        const jobStore = db.createObjectStore(STORES.JOBS, { keyPath: 'id' });
        jobStore.createIndex('scheduled_date', 'scheduled_date');
        jobStore.createIndex('auditor_id', 'auditor_ids', { multiEntry: true });
      }

      // Sites store
      if (!db.objectStoreNames.contains(STORES.SITES)) {
        db.createObjectStore(STORES.SITES, { keyPath: 'id' });
      }

      // Clients store
      if (!db.objectStoreNames.contains(STORES.CLIENTS)) {
        db.createObjectStore(STORES.CLIENTS, { keyPath: 'id' });
      }

      // Services store
      if (!db.objectStoreNames.contains(STORES.SERVICES)) {
        db.createObjectStore(STORES.SERVICES, { keyPath: 'id' });
      }

      // Outbox store - pending sync items
      if (!db.objectStoreNames.contains(STORES.OUTBOX)) {
        const outboxStore = db.createObjectStore(STORES.OUTBOX, { keyPath: 'id', autoIncrement: true });
        outboxStore.createIndex('status', 'status');
        outboxStore.createIndex('created_at', 'created_at');
      }

      // Meta store - sync timestamps, settings
      if (!db.objectStoreNames.contains(STORES.META)) {
        db.createObjectStore(STORES.META, { keyPath: 'key' });
      }

      // Stocktake packages — full offline session data
      if (!db.objectStoreNames.contains(STORES.STOCKTAKE_PACKAGES)) {
        const pkgStore = db.createObjectStore(STORES.STOCKTAKE_PACKAGES, { keyPath: 'id' });
        pkgStore.createIndex('sync_state', 'sync_state');
        pkgStore.createIndex('downloaded_at', 'downloaded_at');
      }

      // Sync queue — generic platform sync items
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const sqStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        sqStore.createIndex('status', 'status');
        sqStore.createIndex('entity_type', 'entity_type');
        sqStore.createIndex('created_at', 'created_at');
      }

      // Count packages — lightweight offline count data
      if (!db.objectStoreNames.contains(STORES.COUNT_PACKAGES)) {
        const countStore = db.createObjectStore(STORES.COUNT_PACKAGES, { keyPath: 'id' });
        countStore.createIndex('sync_state', 'sync_state');
      }

      // Observations — offline observation notes
      if (!db.objectStoreNames.contains(STORES.OBSERVATIONS)) {
        const obsStore = db.createObjectStore(STORES.OBSERVATIONS, { keyPath: 'local_id' });
        obsStore.createIndex('session_id', 'session_id');
        obsStore.createIndex('synced', 'synced');
      }

      // Photo queue — pending photo uploads
      if (!db.objectStoreNames.contains(STORES.PHOTO_QUEUE)) {
        const photoStore = db.createObjectStore(STORES.PHOTO_QUEUE, { keyPath: 'local_id' });
        photoStore.createIndex('observation_local_id', 'observation_local_id');
        photoStore.createIndex('status', 'status');
      }
    };
  });
};

/**
 * Get all items from a store
 */
const getAll = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get item by key
 */
const get = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Put item into store (insert or update)
 */
const put = async (storeName, item) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Put multiple items into store
 */
const putAll = async (storeName, items) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    items.forEach(item => store.put(item));
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Delete item from store
 */
const remove = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all items from a store
 */
const clear = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all offline data (for logout)
 */
const clearAllData = async () => {
  const storesToClear = [
    STORES.CREDENTIALS,
    STORES.JOBS,
    STORES.SITES,
    STORES.CLIENTS,
    STORES.SERVICES,
    STORES.OUTBOX,
    STORES.META
  ];
  
  for (const store of storesToClear) {
    await clear(store);
  }
};

/**
 * Get items from outbox by status
 */
const getOutboxByStatus = async (status) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.OUTBOX, 'readonly');
    const store = transaction.objectStore(STORES.OUTBOX);
    const index = store.index('status');
    const request = index.getAll(status);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Add item to outbox
 */
const addToOutbox = async (item) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORES.OUTBOX, 'readwrite');
    const store = transaction.objectStore(STORES.OUTBOX);
    const request = store.add({
      ...item,
      status: 'pending',
      created_at: new Date().toISOString(),
      attempts: 0
    });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update outbox item status
 */
const updateOutboxStatus = async (id, status, error = null) => {
  const db = await initDB();
  return new Promise(async (resolve, reject) => {
    const item = await get(STORES.OUTBOX, id);
    if (!item) {
      reject(new Error('Outbox item not found'));
      return;
    }

    const transaction = db.transaction(STORES.OUTBOX, 'readwrite');
    const store = transaction.objectStore(STORES.OUTBOX);
    const request = store.put({
      ...item,
      status,
      error,
      attempts: (item.attempts || 0) + 1,
      last_attempt: new Date().toISOString()
    });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const offlineDb = {
  STORES,
  initDB,
  get,
  getAll,
  put,
  putAll,
  remove,
  clear,
  clearAllData,
  getOutboxByStatus,
  addToOutbox,
  updateOutboxStatus
};

export default offlineDb;
